import styled from 'styled-components';

export const PricingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
`;

export const FloatingElement = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  background: linear-gradient(45deg, rgba(197, 51, 123, 0.1), rgba(233, 30, 99, 0.1));
  border-radius: 50%;
  filter: blur(40px);
  animation: float ${props => props.delay || '0s'} 6s ease-in-out infinite;
  top: ${props => Math.random() * 80 + 10}%;
  left: ${props => Math.random() * 80 + 10}%;
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
`;

export const MainContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2B3857;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #2B3857, #C5337B);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #64748B;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const Form = styled.form`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const FormSection = styled.div`
  margin-bottom: 2.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2B3857;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #E2E8F0;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #C5337B;
    box-shadow: 0 0 0 3px rgba(197, 51, 123, 0.1);
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background: white;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #C5337B;
    box-shadow: 0 0 0 3px rgba(197, 51, 123, 0.1);
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

export const Card = styled.div`
  background: #F8FAFC;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const CardTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2B3857;
  margin: 0;
`;

export const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ActionButton = styled.button`
  background: ${props => props.variant === 'danger' ? '#EF4444' : '#C5337B'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'danger' ? '#DC2626' : '#A02A63'};
    transform: translateY(-1px);
  }
`;

export const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const PackageCard = styled(Card)`
  border-color: ${props => props.isMostPopular ? '#C5337B' : '#E2E8F0'};
  border-width: ${props => props.isMostPopular ? '3px' : '2px'};
  position: relative;
  
  &:hover {
    border-color: #C5337B;
    box-shadow: 0 4px 12px rgba(197, 51, 123, 0.2);
  }
`;

export const MostPopularBadge = styled.span`
  background: linear-gradient(135deg, #C5337B, #E91E63);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const PointsContainer = styled.div`
  margin-top: 1rem;
`;

export const Point = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const PointInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #C5337B;
    box-shadow: 0 0 0 2px rgba(197, 51, 123, 0.1);
  }
`;

export const AddButton = styled.button`
  background: #C5337B;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #A02A63;
    transform: translateY(-1px);
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

export const TableHeader = styled.tr`
  background: #F8FAFC;
  border-bottom: 2px solid #E2E8F0;
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #E2E8F0;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #F8FAFC;
  }
`;

export const TableCell = styled.td`
  padding: 1rem;
  text-align: left;
  
  ${props => props.as === 'th' && `
    font-weight: 600;
    color: #2B3857;
  `}

  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

export const ReasonsContainer = styled.div`
  margin-top: 1rem;
`;

export const ReasonInput = styled(PointInput)`
  flex: 1;
`;

export const RequirementsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const RequirementCard = styled(Card)`
  &:hover {
    border-color: #C5337B;
    box-shadow: 0 4px 12px rgba(197, 51, 123, 0.1);
  }
`;

export const RequirementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

export const SubmitButton = styled.button`
  background: linear-gradient(135deg, #C5337B, #E91E63);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 2rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(197, 51, 123, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const ErrorMessage = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #DC2626;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PreviewSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const PreviewTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2B3857;
  margin-bottom: 1.5rem;
  text-align: center;
`;

export const PreviewCard = styled.div`
  background: #F8FAFC;
  border-radius: 12px;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

export const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #C5337B;
`;

